// src/components/ui/Badge.tsx
//
// Small status indicator / label.
// Supports semantic variants for common states.

import { type HTMLAttributes } from "react";
import { cn } from "@/lib/cn";

export type BadgeVariant = "default" | "success" | "warning" | "danger" | "info" | "teal";
export type BadgeSize = "sm" | "md";

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
}

const variantClasses: Record<BadgeVariant, string> = {
  default: "bg-surface-elevated text-fg-secondary border-border",
  success: "bg-accent-green/20 text-accent-green border-accent-green/30",
  warning: "bg-accent-amber/20 text-accent-amber border-accent-amber/30",
  danger: "bg-accent-rose/20 text-accent-rose border-accent-rose/30",
  info: "bg-accent-info/20 text-accent-info border-accent-info/30",
  teal: "bg-benchmark-teal/20 text-benchmark-teal-bright border-benchmark-teal/30",
};

const sizeClasses: Record<BadgeSize, string> = {
  sm: "h-5 px-1.5 text-xs",
  md: "h-6 px-2 text-xs",
};

/**
 * Status badge / label.
 * Use for indicating state (active, pending, failed) or
 * categorization (brand, category).
 */
export function Badge({
  variant = "default",
  size = "md",
  className,
  children,
  ...rest
}: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded border font-510",
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}